import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { currentPlanOf } from "../clientPlan";
import { fetchClientPayments } from "../services/clientPaymentsService";
import type { ClientPayment, ClientPlan } from "../types/clients.types";

export type ClientPlanStatus = "loading" | "ready" | "failed";

export interface ClientPlanState {
  /** Where this client's subscription stands today — see `currentPlanOf`. */
  plan: ClientPlan;
  status: ClientPlanStatus;
  /** The server's reason for a failed load, where it gave one. */
  error?: string;
  /** What the failed banner's retry calls. */
  reload: () => void;
}

/** The same array every time, so the memo below does not rebuild per render. */
const EMPTY: ClientPayment[] = [];

/**
 * The plan a client is on, for the banner above their meal plan list.
 *
 * The same call the Payments tab makes — `GET /clients/{id}/payment-requests/`,
 * every page of it — because a plan is what a paid request bought and there is
 * no other endpoint that knows about one. Made again here rather than shared
 * with that tab: the two are different tabs of the screen, only one is mounted
 * at a time, and a plan bought on another device has to show up on the next
 * visit either way.
 *
 * Failures are reported by the banner itself rather than by a toast: it is one
 * strip of a page that has a table under it, and the table has already said
 * whatever it has to say about its own load.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useClientPlan(clientId: string): ClientPlanState {
  const getToken = useTokenGetter();

  const [payments, setPayments] = useState<ClientPayment[]>(EMPTY);
  const [status, setStatus] = useState<ClientPlanStatus>("loading");
  const [error, setError] = useState<string | undefined>(undefined);
  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    setStatus("loading");

    fetchClientPayments(getToken, clientId, controller.signal)
      .then((loaded) => {
        // Unmounted, or a retry has already replaced this run.
        if (controller.signal.aborted) return;
        setPayments(loaded);
        setError(undefined);
        setStatus("ready");
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setPayments(EMPTY);
        setError(
          cause instanceof ApiError
            ? cause.message
            : "We could not reach the server. Check your connection and try again."
        );
        setStatus("failed");
        console.error("client plan failed to load:", cause);
      });

    return () => controller.abort();
  }, [getToken, clientId, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  // Worked out once per history rather than per render — and against the day
  // the history landed, which is what "13 days left" was counted from.
  const plan = useMemo(() => currentPlanOf(payments), [payments]);

  return { plan, status, error, reload };
}
